import * as THREE from "three";
import * as ZapparThree from "@zappar/zappar-threejs";

interface Uniforms {
  [uniform: string]: THREE.IUniform<any>;
}

export class Pass extends THREE.ShaderMaterial {
  public renderTarget: THREE.WebGLRenderTarget | undefined;

  private scene: THREE.Scene = new THREE.Scene();

  public static uEnabled = { value: true };

  public static uTexture = { value: new THREE.Texture() };

  public static uTextureMatrix = { value: new THREE.Matrix4() };

  public static uResolution = { value: new THREE.Vector3() };

  public static uTime = { value: 0 };

  private renderTargetInitialised = false;

  public tracker?: ZapparThree.FaceTracker;
  
  public faceBufferGeometry = new ZapparThree.FaceBufferGeometry();

  public camera?: ZapparThree.Camera;

  public renderToTarget: boolean;

  private clock = new THREE.Clock();

  public constructor(vertexShader: string, fragmentShader: string, uniforms?: Uniforms) {
    super({
      uniforms: {
        uTexture: Pass.uTexture,
        uTextureMatrix: Pass.uTextureMatrix,
        uResolution: Pass.uResolution,
        uEnabled: Pass.uEnabled,
        uTime: Pass.uTime,
        ...uniforms,
      },
      fragmentShader,
      vertexShader,
      //blending: THREE.MultiplyBlending,
    });
    this.clock.start();
    this.renderToTarget = false;
  }

  private initializeRenderTarget() {
    if (!this.camera) {
      throw new Error("Missing camera.");
    }

    if (!this.faceBufferGeometry) {
      throw new Error("Missing face buffer geometry.");
    }

    if (!this.tracker) {
      throw new Error("Missing tracker.");
    }

    this.renderTarget = new THREE.WebGLRenderTarget(window.innerWidth, window.innerHeight, {
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
      format: THREE.RGBAFormat,
      stencilBuffer: false,
      depthBuffer: true,
    });

    const trackerGroup = new ZapparThree.FaceAnchorGroup(this.camera, this.tracker);
    const faceMeshObject = new THREE.Mesh(this.faceBufferGeometry, this);
    trackerGroup.add(faceMeshObject);
    this.scene.add(trackerGroup);
    this.scene.background = this.camera.backgroundTexture;
    this.renderTargetInitialised = true;
  }

  public render(texture: THREE.Texture, renderer: THREE.WebGLRenderer) {
    this.uniforms.uTime.value = this.clock.getElapsedTime();
    this.uniforms.uTexture.value = texture;
    this.uniforms.uTextureMatrix.value = texture.matrix;
    this.uniforms.uResolution.value.set(renderer.domElement.width, renderer.domElement.height);
    this.uniformsNeedUpdate = true;

    if (this.renderToTarget && !this.renderTargetInitialised) {
      this.initializeRenderTarget();
    }

    if (this.renderTarget && this.renderToTarget) {
      if (!this.camera) {
        throw new Error("Missing camera.");
      }
      this.renderTarget.setSize(renderer.domElement.width, renderer.domElement.height);
      // Render scene_rt into render target.
      renderer.setRenderTarget(this.renderTarget);
      renderer.render(this.scene, this.camera);
      // Reset render target.
      renderer.setRenderTarget(null);
    }
  }

  public dispose = () => {
    super.dispose();
    if (this.renderTarget) {
      this.renderTarget.dispose();
    }
  }
}
